import { t } from "@lingui/macro"
import { makeAutoObservable } from "mobx"
import React from "react"

import { AlertsService } from "src/api"
import { adminv3_AlertListItem } from "src/api/models/adminv3_AlertListItem"
import { Channel } from "src/channel"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { parseDate } from "src/lib/date"
import { createLoadingKeys } from "src/lib/loading"
import { Pagination } from "src/lib/pagination"
import { reportError, reportUnhandledApiError } from "src/lib/report"
import { IAdvanceQueryModel } from "src/types/data-grid-pro"
import { AlertStatus, IAlertListItem } from "src/views/alerts/types"

export class AlertsStore implements IDisposable {
    static Context = React.createContext<AlertsStore | null>(null)
    static LoadingKeys = createLoadingKeys("pageLoad")

    _accessGroupId: number = DEFAULT_ACCESS_GROUP.id

    private repositoryUpdatesListenerDisposer?: () => void

    alerts = new Pagination(
        async (params) => {
            const response = await AlertsService.postV3AdminAlertList({
                request: {
                    access_group_id: this.getAccountGroupId(),
                    page_number: params.page,
                    page_size: params.pageSize,
                    filter: params.advanceQuery,
                },
            })

            const items: IAlertListItem[] =
                response.items?.map((alert: adminv3_AlertListItem) => ({
                    ...alert,
                    id: alert.id ?? -1,
                    title: alert.title as string,
                    message: alert.text as string,
                    createdAt: parseDate(alert.created_at),
                    status: this.getStatus(alert),
                })) ?? []

            return {
                items,
                rawItems: response.items ?? [],
                count: response.total_count ?? 0,
            }
        },
        { loadingKey: AlertsStore.LoadingKeys.pageLoad },
    )

    constructor() {
        makeAutoObservable(this)
    }

    get accessGroupId() {
        return this._accessGroupId
    }

    private setAccessGroup(accessGroupId: number) {
        this._accessGroupId = accessGroupId
    }

    async init(accessGroupId: number, advanceQuery?: IAdvanceQueryModel) {
        this.listenToRepositoryUpdates()
        this.setAccessGroup(accessGroupId)
        try {
            await this.alerts.loadInitialPage(advanceQuery)
        } catch (error) {
            reportError(t`alerts.init-fail`, error)
        }
    }

    dispose() {
        this.repositoryUpdatesListenerDisposer?.()
    }

    async deleteAlert(id: number) {
        try {
            await AlertsService.deleteV3AdminAlert({
                id: id,
            })
        } catch (e) {
            reportUnhandledApiError(e)
        } finally {
            await this.alerts.reload()
        }
    }

    async query(advanceQuery: IAdvanceQueryModel) {
        await this.alerts.loadAdvanceQuery(advanceQuery)
    }

    private getStatus(alert: adminv3_AlertListItem): AlertStatus {
        if (alert.segment_ids != null && alert.segment_ids.length > 0) {
            return "published"
        }
        return "draft"
    }

    private listenToRepositoryUpdates() {
        this.repositoryUpdatesListenerDisposer = Channel.addListener(
            async (event) => {
                if (
                    event.name === "repository/updated" &&
                    event.payload.repository === "alert"
                ) {
                    await this.alerts.reload()
                }
            },
        )
    }

    private getAccountGroupId() {
        return this._accessGroupId !== DEFAULT_ACCESS_GROUP.id
            ? this._accessGroupId
            : undefined
    }
}
